/**
 * Create KATA Cycle Modal Component
 */

import React, { useState, useEffect } from 'react';
import { X, Target, Users, Calendar } from 'lucide-react';
import { useKATABoardStore } from '../../stores/kataBoardStore';
import { useAuthStore } from '../../stores/authStore';

interface CreateCycleModalProps {
  workshopId: number;
  onClose: () => void;
}

const CreateCycleModal: React.FC<CreateCycleModalProps> = ({ workshopId, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    current_condition: '',
    target_condition: '',
    obstacle: '',
    success_criteria: '',
    cycle_number: 1,
    coach_id: '',
    learner_id: '',
    target_completion_date: ''
  });

  const { createCycle, loading } = useKATABoardStore();
  const { user } = useAuthStore();

  useEffect(() => {
    // Set current user as default coach
    if (user) {
      setFormData(prev => ({ ...prev, coach_id: user.id.toString() }));
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      workshop: workshopId,
      coach_id: formData.coach_id ? parseInt(formData.coach_id) : undefined,
      learner_id: formData.learner_id ? parseInt(formData.learner_id) : undefined,
      target_completion_date: formData.target_completion_date || undefined
    };

    await createCycle(submitData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Target className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create New KATA Cycle</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cycle Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Reduce Setup Time for Line A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of the improvement goal..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cycle Number
                </label>
                <input
                  type="number"
                  name="cycle_number"
                  value={formData.cycle_number}
                  onChange={handleChange}
                  min="1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Completion Date
                </label>
                <input
                  type="date"
                  name="target_completion_date"
                  value={formData.target_completion_date}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* KATA Conditions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">KATA Conditions</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Condition *
              </label>
              <textarea
                name="current_condition"
                value={formData.current_condition}
                onChange={handleChange}
                required
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the current state of the process..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Condition *
              </label>
              <textarea
                name="target_condition"
                value={formData.target_condition}
                onChange={handleChange}
                required
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the desired future state..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Obstacle
              </label>
              <textarea
                name="obstacle"
                value={formData.obstacle}
                onChange={handleChange}
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What is preventing you from reaching the target condition?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Success Criteria *
              </label>
              <textarea
                name="success_criteria"
                value={formData.success_criteria}
                onChange={handleChange}
                required
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="How will you measure success?"
              />
            </div>
          </div>

          {/* Team Assignment */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Team Assignment
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coach
                </label>
                <input
                  type="number"
                  name="coach_id"
                  value={formData.coach_id}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Coach User ID"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the user ID of the KATA coach
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learner
                </label>
                <input
                  type="number"
                  name="learner_id"
                  value={formData.learner_id}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Learner User ID"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the user ID of the KATA learner
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Target className="h-4 w-4 mr-2" />
                  Create Cycle
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCycleModal;
